/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import dayjs from 'dayjs'
import { Tooltip } from 'antd'
import colors from '@sportninja/common/constants/appColors'

import type { FillCompletenessStatus } from '../hooks/useSchedulerGeneration'

interface SummaryStats {
  totalGames: number
  totalConflicts: number
  /** Soft, non-blocking advisory count — matches the inline table header. */
  advisories: number
  scheduleScore: string
  /**
   * Fill contract rollup (fill flights only). `avoidable_underfill` ⇒ the
   * schedule left `unusedFillableSlotCount` legally-fillable slots empty (shown
   * as "N fillable slots unused"); `fill_limited_by_setup` ⇒ the setup caps the
   * fill (shown as "Limited by setup: <reason>"); `unknown` ⇒ could not verify;
   * null / `complete` ⇒ no fill note. Null = a non-fill / unclassified division
   * (silent); a KNOWN fill division's pre-feature null is lifted to `unknown`
   * upstream in api2 and DOES show the note.
   */
  fillCompletenessStatus?: FillCompletenessStatus | null
  unusedFillableSlotCount?: number | null
  fillLimitedReason?: string | null
}

interface ScheduleSummaryProps {
  stats?: SummaryStats | null
  loading?: boolean
  onCardClick?: (cardType: string) => void
  startsAt?: string | null
  endsAt?: string | null
  generationElapsedSeconds?: number | null
  children?: React.ReactNode
}

// Trophy icon for Total Games
const TrophyIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 32,
  color = colors.NEUTRAL_50,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19 5H17V3H7V5H5C3.9 5 3 5.9 3 7V8C3 10.55 4.92 12.63 7.39 12.94C8.02 14.44 9.37 15.57 11 15.9V19H7V21H17V19H13V15.9C14.63 15.57 15.98 14.44 16.61 12.94C19.08 12.63 21 10.55 21 8V7C21 5.9 20.1 5 19 5ZM5 8V7H7V10.82C5.84 10.4 5 9.3 5 8ZM12 14C10.35 14 9 12.65 9 11V5H15V11C15 12.65 13.65 14 12 14ZM19 8C19 9.3 18.16 10.4 17 10.82V7H19V8Z'
      fill={color}
    />
  </svg>
)

// Error/Conflict circle icon
const ConflictCircleIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 32,
  color = colors.NEUTRAL_50,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM11 7H13V13H11V7ZM11 15H13V17H11V15Z'
      fill={color}
    />
  </svg>
)

// Lightbulb icon for Advisories — a calm "optional suggestions" cue rather
// than an alarm triangle.
const LightbulbIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 32,
  color = colors.NEUTRAL_50,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M9 21c0 .55.45 1 1 1h4c.55 0 1-.45 1-1v-1H9v1zm3-19C8.14 2 5 5.14 5 9c0 2.38 1.19 4.47 3 5.74V17c0 .55.45 1 1 1h6c.55 0 1-.45 1-1v-2.26c1.81-1.27 3-3.36 3-5.74 0-3.86-3.14-7-7-7z'
      fill={color}
    />
  </svg>
)

// Speed/Score icon for Schedule Score
const SpeedIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 32,
  color = colors.NEUTRAL_50,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 32 32'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M19.9867 14.6667H17.3334V6.66669L10.6667 18.6667H13.3334V26.6667L19.9867 14.6667ZM16.0001 2.66669C8.63608 2.66669 2.66675 8.63602 2.66675 16C2.66675 23.364 8.63608 29.3334 16.0001 29.3334C23.3641 29.3334 29.3334 23.364 29.3334 16C29.3334 8.63602 23.3641 2.66669 16.0001 2.66669ZM16.0001 26.6667C10.1067 26.6667 5.33341 21.8934 5.33341 16C5.33341 10.1067 10.1067 5.33335 16.0001 5.33335C21.8934 5.33335 26.6667 10.1067 26.6667 16C26.6667 21.8934 21.8934 26.6667 16.0001 26.6667Z'
      fill={color}
    />
  </svg>
)

// Helper to determine score color
const getScoreColor = (score: string): string => {
  const lower = score.toLowerCase()
  if (lower === 'good' || lower === 'excellent') {
    return colors.SUCCESS_100
  }
  return colors.WARNING_300
}

// Subtitle stays robust for ARBITRARY label strings (e.g. the analytics page
// passes 'ACCEPTABLE'): unknown words fall through to the default. When counts
// are present it names WHY the word is capped, so the score reads honestly next
// to its badges. The cap itself lives in getScoreLabel's callers, never here.
const getScoreSubtitle = (
  score: string,
  conflicts = 0,
  advisories = 0,
  fillNote: string | null = null
): string => {
  if (conflicts > 0) {
    return `Held back by ${conflicts} conflict${conflicts === 1 ? '' : 's'}`
  }
  if (advisories > 0) {
    return `Held back by ${advisories} violation${advisories === 1 ? '' : 's'}`
  }
  // With no conflicts/advisories, the fill contract is the next thing that can
  // cap the word — name it so the score reads honestly (e.g. "GOOD" + "held
  // back by N unused slots").
  if (fillNote) {
    return fillNote
  }
  const lower = score.toLowerCase()
  if (lower === 'excellent') {
    return 'Great schedule quality'
  }
  if (lower === 'good') {
    return 'Good schedule quality'
  }
  return 'Review violations below'
}

/**
 * Fill-contract note for a fill flight, or null when there is nothing to say:
 * `complete` and null (a non-fill / unclassified division) stay silent. A KNOWN
 * fill division's pre-feature null is lifted to `unknown` upstream and DOES show
 * a note. Drives both the lead secondary copy and the score-card subtitle so the
 * cap is explained once, consistently.
 */
const getFillNote = (stats: SummaryStats): string | null => {
  switch (stats.fillCompletenessStatus) {
    case 'avoidable_underfill': {
      const n = stats.unusedFillableSlotCount ?? 0
      return n > 0
        ? `${n} fillable slot${
            n === 1 ? '' : 's'
          } unused — re-generate or relax a constraint to fill them`
        : 'Some fillable slots are unused — re-generate or relax a constraint to fill them'
    }
    case 'fill_limited_by_setup':
      return stats.fillLimitedReason
        ? `Limited by setup: ${stats.fillLimitedReason}`
        : 'Fill limited by the current setup'
    case 'unknown':
      return 'Fill completeness could not be verified'
    default:
      return null
  }
}

interface LeadCopy {
  primary: string
  secondary: string
  tone: 'calm' | 'attention'
}

// Neutral lead that leads with the positive outcome. Deliberately number-free
// for advisories (so the 84-vs-168 representations never look contradictory)
// and makes NO publish-readiness claim — zero conflicts does not equal
// publishable (that requires the flight to be applied).
const getLeadCopy = (stats: SummaryStats): LeadCopy => {
  const games = `${stats.totalGames} game${stats.totalGames === 1 ? '' : 's'}`
  if (stats.totalConflicts > 0) {
    return {
      primary: `${stats.totalConflicts} conflict${
        stats.totalConflicts === 1 ? '' : 's'
      } need attention · ${games} scheduled.`,
      secondary: 'Resolve conflicts below before publishing.',
      tone: 'attention',
    }
  }
  // With no conflicts, surface the fill-contract note next (avoidable underfill
  // is the most actionable thing left). Falls back to the advisory/ready copy.
  const fillNote = getFillNote(stats)
  return {
    primary: `No conflicts found · ${games} scheduled.`,
    secondary:
      fillNote ??
      (stats.advisories > 0
        ? 'Optional violations are flagged on individual games below ↓'
        : 'Your schedule is ready to review.'),
    tone: 'calm',
  }
}

/**
 * Global Summary Component
 * Displays summary statistics for the generated schedule
 * Matches Figma node 15859:30772
 */
const formatElapsed = (totalSeconds: number): string => {
  const mins = Math.floor(totalSeconds / 60)
  const secs = totalSeconds % 60
  if (mins === 0) {
    return `${secs}s`
  }
  return `${mins}m ${String(secs).padStart(2, '0')}s`
}

const ScheduleSummary: React.FC<ScheduleSummaryProps> = ({
  stats,
  loading = false,
  onCardClick,
  startsAt,
  endsAt,
  generationElapsedSeconds,
  children,
}) => {
  const collapsed = false

  const displayStats: SummaryStats = stats ?? {
    totalGames: 0,
    totalConflicts: 0,
    advisories: 0,
    scheduleScore: '--',
  }
  const containerStyles = css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  `

  const headerStyles = css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
  `

  const leadStyles = (tone: 'calm' | 'attention') => css`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    padding: 12px 16px;
    border-radius: 8px;
    background: ${colors.SECONDARY_300};
    border-left: 3px solid
      ${tone === 'attention' ? colors.ERROR_200 : colors.SUCCESS_100};
  `

  const leadPrimaryStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 20px;
    color: ${colors.WHITE};
    margin: 0;
  `

  const leadSecondaryStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 17px;
    color: ${colors.NEUTRAL_70};
    margin: 0;
  `

  const sectionTitleStyles = css`
    flex: 1;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 16.8px;
    letter-spacing: 1.68px;
    text-transform: uppercase;
    color: ${colors.WHITE};
    margin: 0;
  `

  const cardsContainerStyles = css`
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
    width: 100%;
  `

  const indicatorCardStyles = (clickable: boolean) => css`
    display: flex;
    gap: 6px;
    align-items: flex-end;
    justify-content: center;
    padding: 8px 16px;
    background: ${colors.SECONDARY_300};
    border-radius: 8px;
    flex: 1 0 0;
    min-width: 200px;
    height: 82px;
    position: relative;
    cursor: ${clickable ? 'pointer' : 'default'};
    transition: background 0.15s ease;
    border: none;
    text-align: left;

    ${clickable ? `&:hover { background: ${colors.SECONDARY_200}; }` : ''}
  `

  const cardContentStyles = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
  `

  const cardTitleStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 700;
    font-size: 16px;
    line-height: 23.2px;
    color: ${colors.PRIMARY};
    margin: 0;
    width: 100%;
  `

  const cardValueStyles = css`
    font-family: 'Barlow Condensed', sans-serif;
    font-weight: 600;
    font-size: 20px;
    line-height: 24px;
    color: ${colors.WHITE};
    margin: 0;
    width: 100%;
  `

  const cardSubtitleStyles = css`
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 17.4px;
    color: ${colors.NEUTRAL_50};
    margin: 0;
  `

  const cardIconStyles = css`
    flex-shrink: 0;
  `

  const scoreColor = getScoreColor(displayStats.scheduleScore)

  const loadingPulseStyles = css`
    @keyframes summaryPulse {
      0%,
      100% {
        opacity: 0.5;
      }
      50% {
        opacity: 1;
      }
    }
    animation: summaryPulse 1.5s ease-in-out infinite;
  `

  return (
    <div css={containerStyles}>
      {/* Header */}
      <div css={headerStyles}>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
          `}
        >
          <p css={sectionTitleStyles}>GLOBAL SUMMARY</p>
        </div>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          {generationElapsedSeconds != null && (
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 500;
                font-size: 12px;
                color: ${colors.NEUTRAL_70};
                background: ${colors.SECONDARY_200};
                padding: 3px 10px;
                border-radius: 12px;
              `}
            >
              Generated in {formatElapsed(generationElapsedSeconds)}
            </span>
          )}
          {startsAt && endsAt && (
            <p
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 400;
                font-size: 13px;
                line-height: 16.8px;
                color: ${colors.NEUTRAL_50};
                margin: 0;
              `}
            >
              {dayjs(startsAt).format('MMM D, YYYY')} –{' '}
              {dayjs(endsAt).format('MMM D, YYYY')}
            </p>
          )}
        </div>
      </div>

      {/* Calm lead — leads with the outcome, no publish claim, number-free
          for advisories. Only shown once real stats are in. */}
      {stats && !loading && (
        <div css={leadStyles(getLeadCopy(displayStats).tone)}>
          <p css={leadPrimaryStyles}>{getLeadCopy(displayStats).primary}</p>
          <p css={leadSecondaryStyles}>{getLeadCopy(displayStats).secondary}</p>
        </div>
      )}

      {/* Indicator Cards */}
      {!collapsed && (
        <div css={[cardsContainerStyles, loading && loadingPulseStyles]}>
          {/* Total Games Card */}
          <Tooltip
            title='Total number of games generated across all divisions'
            placement='bottom'
          >
            <div
              css={indicatorCardStyles(!loading && displayStats.totalGames > 0)}
              onClick={() =>
                !loading &&
                displayStats.totalGames > 0 &&
                onCardClick?.('totalGames')
              }
              role='button'
              tabIndex={0}
            >
              <div css={cardContentStyles}>
                <p css={cardTitleStyles}>Total Games</p>
                <p css={cardValueStyles}>
                  {loading ? '--' : `${displayStats.totalGames} Games`}
                </p>
                <p css={cardSubtitleStyles}>
                  {loading
                    ? 'Generating...'
                    : displayStats.totalGames > 0
                    ? 'Click to see all scheduled'
                    : 'No games generated'}
                </p>
              </div>
              <div css={cardIconStyles}>
                <TrophyIcon size={32} color={colors.NEUTRAL_50} />
              </div>
            </div>
          </Tooltip>

          {/* Total Conflicts Card */}
          <Tooltip
            title='Schedule conflicts (errors) that need to be resolved before publishing'
            placement='bottom'
          >
            <div
              css={indicatorCardStyles(
                !loading && displayStats.totalConflicts > 0
              )}
              onClick={() =>
                !loading &&
                displayStats.totalConflicts > 0 &&
                onCardClick?.('totalConflicts')
              }
              role='button'
              tabIndex={0}
            >
              <div css={cardContentStyles}>
                <p css={cardTitleStyles}>Total Conflicts</p>
                <p css={cardValueStyles}>
                  {loading ? '--' : displayStats.totalConflicts}
                </p>
                <p css={cardSubtitleStyles}>
                  {loading
                    ? 'Generating...'
                    : displayStats.totalConflicts > 0
                    ? 'Click to resolve'
                    : 'No conflicts'}
                </p>
              </div>
              <div css={cardIconStyles}>
                <ConflictCircleIcon size={32} color={colors.NEUTRAL_50} />
              </div>
            </div>
          </Tooltip>

          {/* Violations Card */}
          <Tooltip
            title='Optional, non-blocking suggestions that may improve schedule quality'
            placement='bottom'
          >
            <div
              css={indicatorCardStyles(!loading && displayStats.advisories > 0)}
              onClick={() =>
                !loading &&
                displayStats.advisories > 0 &&
                onCardClick?.('advisories')
              }
              role='button'
              tabIndex={0}
            >
              <div css={cardContentStyles}>
                <p css={cardTitleStyles}>Violations</p>
                <p css={cardValueStyles}>
                  {loading ? '--' : displayStats.advisories}
                </p>
                <p css={cardSubtitleStyles}>
                  {loading
                    ? 'Generating...'
                    : displayStats.advisories === 0
                    ? 'No violations'
                    : 'Optional — won’t block'}
                </p>
              </div>
              <div css={cardIconStyles}>
                <LightbulbIcon size={32} color={colors.NEUTRAL_50} />
              </div>
            </div>
          </Tooltip>

          {/* Schedule Score Card */}
          <Tooltip
            title='Overall schedule quality based on constraint satisfaction'
            placement='bottom'
          >
            <div css={indicatorCardStyles(false)} role='button' tabIndex={0}>
              <div css={cardContentStyles}>
                <p css={cardTitleStyles}>Schedule Score</p>
                <p
                  css={css`
                    ${cardValueStyles};
                    color: ${loading ? colors.NEUTRAL_50 : scoreColor};
                  `}
                >
                  {loading ? '--' : displayStats.scheduleScore}
                </p>
                <p css={cardSubtitleStyles}>
                  {loading
                    ? 'Generating...'
                    : getScoreSubtitle(
                        displayStats.scheduleScore,
                        displayStats.totalConflicts,
                        displayStats.advisories,
                        getFillNote(displayStats)
                      )}
                </p>
              </div>
              <div css={cardIconStyles}>
                <SpeedIcon size={32} color={colors.NEUTRAL_50} />
              </div>
            </div>
          </Tooltip>
        </div>
      )}

      {/* Additional content (charts, etc.) inside collapsible area */}
      {!collapsed && children}
    </div>
  )
}

export default ScheduleSummary
